import React, { useState, useEffect, useContext, useMemo } from 'react';
import api from '../../services/api';
import { fechaEcuador, hoyEcuador, haceDiasEcuador, inicioMesEcuador } from '../../utils/fechas';
import printerService from '../../services/printerService';
import billingService from '../../services/billingService';
import { AuthContext } from '../../context/AuthContext';
import { formatOrderNotes } from '../../utils/formatNotes';
import ConfiguracionSecuenciaModal from './ConfiguracionSecuenciaModal';

// Cantidad de tarjetas que se muestran por cada "Cargar más"
const PAGE_SIZE = 36;

// Estado de pago de una orden para las pestañas y tarjetas
const isCancelledOrder = (o) => String(o.status || '').toLowerCase() === 'cancelled';
const isPaidOrder = (o) => !isCancelledOrder(o) && o.payment_status === 'paid';
const pendingBalance = (o) => (isCancelledOrder(o) ? 0 : parseFloat(o.pending_amount) || 0);

const Ordenes = () => {
    const { user } = useContext(AuthContext);
    const roleName = user?.role_details?.name;
    const isAdmin = roleName === 'SUPER_ADMIN' || roleName === 'ADMIN_FAST_FOOD' || user?.is_superuser;

    const exchangeRate = parseFloat(localStorage.getItem('usdExchangeRate')) || 4000;
    const formatOrderValue = (val, method) => {
        const isCop = method && typeof method === 'string' && method.toLowerCase().includes('cop');
        const numericVal = parseFloat(val) || 0;
        if (isCop) {
            return `COP ${(numericVal * exchangeRate).toLocaleString('es-CO')}`;
        }
        return `$${numericVal.toFixed(2)}`;
    };

    const getPaymentMethodBadge = (method) => {
        if (!method) return <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', backgroundColor: '#f3f4f6', color: '#6b7280', border: '1px solid #e5e7eb' }}>Sin definir</span>;

        const m = String(method).toLowerCase();
        
        if (m.includes('dividido') || m.includes('mixto')) {
            return <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', backgroundColor: '#fef3c7', color: '#b45309', border: '1px solid #fde68a', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>Mixto</span>;
        }
        if (m === 'saldo' || m === 'saldo_favor') {
            return <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', backgroundColor: '#ede9fe', color: '#6d28d9', border: '1px solid #ddd6fe', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>Saldo a favor</span>;
        }
        if (m.includes('efectivo_cop')) {
            return <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', backgroundColor: '#ffedd5', color: '#c2410c', border: '1px solid #fed7aa', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>Efectivo (COP)</span>;
        }
        if (m.includes('transferencia_cop')) {
            return <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', backgroundColor: '#e0f2fe', color: '#0369a1', border: '1px solid #7dd3fc', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>Transfer (COP)</span>;
        }
        if (m.includes('efectivo') || m.includes('cash')) {
            return <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', backgroundColor: '#dcfce7', color: '#15803d', border: '1px solid #86efac', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>Efectivo</span>;
        }
        if (m.includes('transferencia') || m.includes('transfer')) {
            return <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', backgroundColor: '#dbeafe', color: '#1e40af', border: '1px solid #93c5fd', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>Transferencia</span>;
        }
        if (m.includes('tarjeta') || m.includes('card')) {
            return <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', backgroundColor: '#f3e8ff', color: '#6b21a8', border: '1px solid #d8b4fe', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>Tarjeta</span>;
        }

        return <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', backgroundColor: '#f3f4f6', color: '#4b5563', border: '1px solid #e5e7eb' }}>{method}</span>;
    };

    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [updatingStatus, setUpdatingStatus] = useState({});
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [isEditingPayment, setIsEditingPayment] = useState(false);
    const [newPaymentMethod, setNewPaymentMethod] = useState('');
    // Filtros de la vista de tarjetas
    const [statusTab, setStatusTab] = useState('todas');
    const [lineFilter, setLineFilter] = useState('todas');
    const [datePreset, setDatePreset] = useState('hoy');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

    // Estado para configuración de secuencia / turno de orden
    const [showSequenceModal, setShowSequenceModal] = useState(false);
    const [sequenceNumber, setSequenceNumber] = useState('');
    const [savingSequence, setSavingSequence] = useState(false);

    // Estado para la calculadora de órdenes seleccionadas
    const [selectedOrderIds, setSelectedOrderIds] = useState([]);

    const toggleSelectOrder = (orderId) => {
        setSelectedOrderIds(prev =>
            prev.includes(orderId) ? prev.filter(id => id !== orderId) : [...prev, orderId]
        );
    };

    const toggleSelectAll = (currentPaginated) => {
        const currentIds = currentPaginated.map(o => o.id);
        const allSelected = currentIds.every(id => selectedOrderIds.includes(id));
        if (allSelected) {
            setSelectedOrderIds(prev => prev.filter(id => !currentIds.includes(id)));
        } else {
            setSelectedOrderIds(prev => Array.from(new Set([...prev, ...currentIds])));
        }
    };

    const clearSelection = () => {
        setSelectedOrderIds([]);
    };



    const fetchOrders = async () => {
        try {
            const response = await api.get('/api/orders/orders/', {
                baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
            });
            setOrders(response.data.results || response.data || []);
        } catch (err) {
            console.error('Error fetching orders:', err);
            setError('Error al cargar las órdenes');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders();
    }, []);


    // Al cambiar cualquier filtro se vuelve a mostrar desde la primera "página" de tarjetas
    useEffect(() => {
        setVisibleCount(PAGE_SIZE);
    }, [searchTerm, statusTab, lineFilter, datePreset, dateFrom, dateTo]);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && showModal) {
                closeModal();
            }
        };

        if (showModal) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [showModal]);

    const handleChangePaymentMethod = async () => {
        if (!newPaymentMethod) return;
        
        try {
            const response = await api.post(
                `/api/orders/orders/${selectedOrder.order_number}/change_payment_method/`,
                { payment_method: newPaymentMethod },
                { baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE }
            );

            // Actualizar la orden seleccionada
            setSelectedOrder(prev => ({ ...prev, ...response.data }));
            
            // Actualizar la orden en la lista principal
            setOrders(prevOrders =>
                prevOrders.map(order =>
                    order.order_number === selectedOrder.order_number
                        ? { ...order, ...response.data }
                        : order
                )
            );
            
            setIsEditingPayment(false);
        } catch (err) {
            console.error('Error changing payment method:', err);
            alert(`Error al cambiar método de pago: ${err.response?.data?.error || err.message}`);
        }
    };

    const handleStatusChange = async (orderNumber, newStatus, event) => {
        event.stopPropagation();
        setUpdatingStatus(prev => ({ ...prev, [orderNumber]: true }));
        try {
            const response = await api.post(
                `/api/orders/orders/${orderNumber}/update_status/`,
                { status: newStatus },
                { baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE }
            );

            console.log('Status update successful:', response.data);

            setOrders(prevOrders =>
                prevOrders.map(order =>
                    order.order_number === orderNumber
                        ? { ...order, ...response.data }
                        : order
                )
            );
        } catch (err) {
            console.error('Error updating status:', err);
            console.error('Error details:', err.response?.data);
            alert(`Error al actualizar el estado: ${err.response?.data?.detail || err.message}`);
        } finally {
            setUpdatingStatus(prev => ({ ...prev, [orderNumber]: false }));
        }
    };

    const handleRowClick = async (order) => {
        setShowModal(true);
        setSelectedOrder({ ...order, loading: true });

        try {
            const response = await api.get(
                `/api/orders/orders/${order.order_number}/`,
                { baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE }
            );

            let orderData = response.data;
            orderData.customer_name = orderData.customer_name || order.customer_name;

            // Si tiene comprobante emitido pero aún no está en AUTHORIZED, auto-consultar a KoaDoc
            if (orderData.sri_id && orderData.sri_status !== 'AUTHORIZED') {
                try {
                    const statusRes = await billingService.getOrderInvoiceStatus(orderData.id);
                    if (statusRes.success && statusRes.data) {
                        orderData = {
                            ...orderData,
                            sri_status: statusRes.data.status || orderData.sri_status,
                            sri_pdf_url: statusRes.data.pdf_file_url || orderData.sri_pdf_url,
                            sri_xml_url: statusRes.data.xml_file_url || orderData.sri_xml_url,
                            sri_document_number: statusRes.data.document_number || orderData.sri_document_number,
                            sri_access_key: statusRes.data.access_key || orderData.sri_access_key
                        };
                        // Actualizar en el estado de la lista también
                        setOrders(prev => prev.map(o => o.id === orderData.id ? { ...o, ...orderData } : o));
                    }
                } catch (e) {
                    console.warn('No se pudo refrescar estado SRI en vivo:', e);
                }
            }

            setSelectedOrder(orderData);
        } catch (err) {
            console.error('Error fetching order details:', err);
            alert('Error al cargar los detalles de la orden');
            setShowModal(false);
            setSelectedOrder(null);
        }
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedOrder(null);
    };

    const handleCancelOrder = async () => {
        if (!selectedOrder) return;

        const reason = window.prompt(`¿Está seguro de anular la Orden ${selectedOrder.order_number}?\nPor favor, ingrese el motivo de la anulación:`);
        
        if (reason === null) {
            return; // El usuario canceló el prompt
        }
        
        if (reason.trim() === '') {
            alert('Debe ingresar un motivo para anular la orden.');
            return;
        }

        try {
            const response = await api.post(`/api/orders/orders/${selectedOrder.order_number}/cancel/`, {
                reason: reason
            }, {
                baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
            });

            // Actualizar del estado local en vez de eliminarlo
            setOrders(prev => prev.map(o => 
                o.id === selectedOrder.id ? { ...o, ...response.data } : o
            ));
            alert('Orden anulada exitosamente');
            closeModal();
        } catch (err) {
            console.error('Error cancelling order:', err);
            alert(`Error al anular la orden: ${err.response?.data?.detail || err.message}`);
        }
    };

    const handlePrintTicket = async () => {
        if (!selectedOrder) return;

        try {
            const receiptData = {
                order_number: selectedOrder.order_number,
                order_sequence: selectedOrder.order_sequence,
                customer_name: selectedOrder.customer_name || 'CONSUMIDOR FINAL',
                table_number: selectedOrder.table_number || (selectedOrder.order_type === 'takeout' ? 'PARA LLEVAR' : 'MESA GENÉRICA'),
                items: selectedOrder.items.map(item => ({
                    name: item.product_details?.name || item.product_name || 'Producto',
                    quantity: item.quantity,
                    price: parseFloat(item.unit_price),
                    total: parseFloat(item.line_total || item.subtotal),
                    note: item.notes || ''
                })),
                subtotal: parseFloat(selectedOrder.subtotal),
                discount: parseFloat(selectedOrder.discount_amount || 0),
                tax: parseFloat(selectedOrder.tax_amount || 0),
                total: parseFloat(selectedOrder.total),
                payment_method: selectedOrder.payment_method,
                payment_reference: selectedOrder.payment_reference,
                sri_access_key: selectedOrder.sri_access_key,
                sri_document_number: selectedOrder.sri_document_number
            };

            await printerService.printReceipt(receiptData);
            alert('Ticket enviado a la impresora');
        } catch (error) {
            console.error('Error printing ticket:', error);
            alert('Error al imprimir el ticket. Verifique la conexión con el agente de impresión.');
        }
    };

    const handleOpenSequenceModal = () => {
        setShowSequenceModal(true);
    };





    const getStatusDisplay = (status) => {
        if (!status) return '';
        const statusMap = {
            'pending': 'Pendiente',
            'completed': 'Completado',
            'cancelled': 'Anulada'
        };
        return statusMap[status] || status;
    };



    // =====================================
    // FILTROS: pestaña de estado, línea de negocio, fechas y búsqueda
    // =====================================

    // Fechas siempre en hora de Ecuador, sin importar la zona horaria del equipo
    const todayKey = hoyEcuador();

    const dateRange = useMemo(() => {
        switch (datePreset) {
            case 'hoy': return [todayKey, todayKey];
            case 'ayer': return [haceDiasEcuador(1), haceDiasEcuador(1)];
            case '7d': return [haceDiasEcuador(6), todayKey];
            case 'mes': return [inicioMesEcuador(), todayKey];
            case 'rango': return [dateFrom || '0000-01-01', dateTo || '9999-12-31'];
            default: return null;
        }
    }, [datePreset, dateFrom, dateTo, todayKey]);

    // Órdenes que cumplen fecha, línea y búsqueda (las pestañas se cuentan sobre este conjunto)
    const baseFilteredOrders = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        return orders.filter(order => {
            if (dateRange) {
                const key = fechaEcuador(order.created_at);
                if (key < dateRange[0] || key > dateRange[1]) return false;
            }
            if (lineFilter !== 'todas' && (order.business_type || 'cafeteria') !== lineFilter) return false;
            if (!term) return true;
            return [
                order.order_number,
                order.order_sequence ? `#${order.order_sequence}` : '',
                order.customer_name,
                order.table_number,
                order.items_summary,
                order.school_name,
                order.seller_name
            ].some(field => field && String(field).toLowerCase().includes(term));
        });
    }, [orders, dateRange, lineFilter, searchTerm]);

    const tabCounts = useMemo(() => ({
        todas: baseFilteredOrders.length,
        pagadas: baseFilteredOrders.filter(isPaidOrder).length,
        por_cobrar: baseFilteredOrders.filter(o => !isCancelledOrder(o) && !isPaidOrder(o)).length,
        anuladas: baseFilteredOrders.filter(isCancelledOrder).length
    }), [baseFilteredOrders]);

    const sortedAndFilteredOrders = useMemo(() => baseFilteredOrders
        .filter(order => {
            if (statusTab === 'pagadas') return isPaidOrder(order);
            if (statusTab === 'por_cobrar') return !isCancelledOrder(order) && !isPaidOrder(order);
            if (statusTab === 'anuladas') return isCancelledOrder(order);
            return true;
        })
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
    [baseFilteredOrders, statusTab]);

    const visibleOrders = sortedAndFilteredOrders.slice(0, visibleCount);

    // Agrupar por día para mostrar las tarjetas separadas por fecha
    const ordersByDay = useMemo(() => {
        const groups = [];
        visibleOrders.forEach(order => {
            const key = fechaEcuador(order.created_at);
            let group = groups[groups.length - 1];
            if (!group || group.key !== key) {
                group = { key, date: new Date(order.created_at), orders: [] };
                groups.push(group);
            }
            group.orders.push(order);
        });
        return groups;
    }, [visibleOrders]);

    const dayTotals = useMemo(() => {
        const totals = {};
        sortedAndFilteredOrders.forEach(o => {
            const key = fechaEcuador(o.created_at);
            if (!totals[key]) totals[key] = { count: 0, total: 0 };
            totals[key].count += 1;
            if (!isCancelledOrder(o)) totals[key].total += parseFloat(o.total) || 0;
        });
        return totals;
    }, [sortedAndFilteredOrders]);

    const summary = useMemo(() => {
        const active = sortedAndFilteredOrders.filter(o => !isCancelledOrder(o));
        const total = active.reduce((acc, o) => acc + (parseFloat(o.total) || 0), 0);
        const pending = active.reduce((acc, o) => acc + pendingBalance(o), 0);
        return { count: sortedAndFilteredOrders.length, total, pending, collected: total - pending };
    }, [sortedAndFilteredOrders]);

    const dayLabel = (group) => {
        const longDate = group.date.toLocaleDateString('es-EC', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'America/Guayaquil' });
        if (group.key === todayKey) return `Hoy · ${longDate}`;
        if (group.key === haceDiasEcuador(1)) return `Ayer · ${longDate}`;
        return longDate.charAt(0).toUpperCase() + longDate.slice(1);
    };

    const selectedOrdersList = orders.filter(o => selectedOrderIds.includes(o.id));
    const selectedCount = selectedOrdersList.length;
    const selectedTotalUSD = selectedOrdersList.reduce((acc, o) => acc + (parseFloat(o.total) || 0), 0);

    const selectedCashUSD = selectedOrdersList.reduce((acc, o) => {
        const method = String(o.payment_method).toLowerCase();
        if (method.includes('efectivo') || method.includes('cash')) {
            return acc + (parseFloat(o.total) || 0);
        } else if (method.includes('dividido') || method.includes('mixto')) {
            return acc + (parseFloat(o.cash_amount) || 0);
        }
        return acc;
    }, 0);

    const selectedTransferUSD = selectedOrdersList.reduce((acc, o) => {
        const method = String(o.payment_method).toLowerCase();
        if (method.includes('transferencia') || method.includes('transfer')) {
            return acc + (parseFloat(o.total) || 0);
        } else if (method.includes('dividido') || method.includes('mixto')) {
            return acc + (parseFloat(o.transfer_amount) || 0);
        }
        return acc;
    }, 0);

    const selectedCardUSD = selectedOrdersList.reduce((acc, o) => {
        const method = String(o.payment_method).toLowerCase();
        if (method.includes('tarjeta') || method.includes('card')) {
            return acc + (parseFloat(o.total) || 0);
        } else if (method.includes('dividido') || method.includes('mixto')) {
            return acc + (parseFloat(o.card_amount) || 0);
        }
        return acc;
    }, 0);

    const isAllSelected = visibleOrders.length > 0 && visibleOrders.every(o => selectedOrderIds.includes(o.id));

    // Estado visual de cada tarjeta
    const getCardState = (order) => {
        if (isCancelledOrder(order)) return { label: 'ANULADA', color: '#dc2626', bg: '#fee2e2' };
        if (isPaidOrder(order)) return { label: 'PAGADA', color: '#15803d', bg: '#dcfce7' };
        if (pendingBalance(order) > 0) return { label: 'POR COBRAR', color: '#b45309', bg: '#fef3c7' };
        return { label: 'PENDIENTE', color: '#2563eb', bg: '#dbeafe' };
    };

    // Línea de negocio de la orden: se muestra en lugar del tipo de pedido (Delivery, Para llevar...)
    const GRIND_LABELS = { grano: 'En grano', fino: 'Molido fino', medio: 'Molido medio', grueso: 'Molido grueso' };
    const getLineInfo = (order) => {
        const type = order.business_type || 'cafeteria';
        if (type === 'lonchera') {
            const count = order.lunchbox_count ? `${order.lunchbox_count} loncheras` : '';
            return {
                label: 'Loncheras', icon: 'bi-buildings', color: '#c2410c', bg: '#ffedd5',
                detail: [order.school_name ? `Escuela: ${order.school_name}` : 'Escuela sin asignar', count].filter(Boolean).join(' · ')
            };
        }
        if (type === 'cafe_grano') {
            const grams = parseFloat(order.coffee_grams) || 0;
            return {
                label: 'Café en Grano', icon: 'bi-cup-hot', color: '#7c2d12', bg: '#f5e6dc',
                detail: [grams ? `${grams.toLocaleString('es-EC')} g` : '', GRIND_LABELS[order.grind_type] || order.grind_type].filter(Boolean).join(' · ') || 'Café en grano'
            };
        }
        const place = order.order_type === 'takeout' ? 'Para llevar'
            : order.table_number && order.table_number !== 'GENERICA' ? `Mesa ${order.table_number}`
            : 'En local';
        return { label: 'Punto de Venta', icon: 'bi-shop', color: '#1d4ed8', bg: '#dbeafe', detail: place };
    };

    const getSriInfo = (order) => {
        if (order.sri_status === 'AUTHORIZED') return { icon: 'bi-patch-check-fill', text: `Factura ${order.sri_document_number || 'autorizada'}`, color: '#15803d', href: order.sri_pdf_url };
        if (['QUEUED', 'SIGNED', 'SENT', 'PROCESSING'].includes(order.sri_status)) return { icon: 'bi-clock-history', text: 'Factura en proceso SRI', color: '#0369a1' };
        if (order.sri_status === 'REJECTED' || order.sri_status === 'ERROR') return { icon: 'bi-exclamation-octagon-fill', text: 'Error SRI', color: '#b91c1c', title: order.sri_error_message };
        if (order.sri_id) return { icon: 'bi-file-earmark-text', text: `Factura ${order.sri_document_number || 'emitida'}`, color: '#92400e' };
        return { icon: 'bi-receipt', text: 'Nota de entrega', color: '#64748b' };
    };

    const formatTime = (value) => new Date(value).toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Guayaquil' });
    const formatShortDateTime = (value) => new Date(value).toLocaleString('es-EC', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'America/Guayaquil' });

    if (loading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', backgroundColor: '#f8fafc' }}>
                <div style={{ textAlign: 'center' }}>
                    <div style={{
                        display: 'inline-block',
                        width: '48px',
                        height: '48px',
                        border: '4px solid #e5e7eb',
                        borderTopColor: 'var(--primary-color, #4a1f0c)',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                    }}></div>
                    <p style={{ marginTop: '16px', color: '#4b5563', fontWeight: '500' }}>Cargando órdenes...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', backgroundColor: '#f8fafc' }}>
                <div style={{ backgroundColor: '#fef2f2', borderLeft: '4px solid #ef4444', padding: '24px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', maxWidth: '448px' }}>
                    <p style={{ color: '#991b1b', fontWeight: '500' }}>{error}</p>
                </div>
            </div>
        );
    }

    const panelStyle = { backgroundColor: 'white', borderRadius: '14px', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(15,23,42,0.06)' };
    const chipStyle = (active) => ({
        padding: '7px 14px', borderRadius: '999px', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
        border: `1px solid ${active ? 'var(--primary-color, #4a1f0c)' : '#e5e7eb'}`,
        background: active ? 'var(--primary-color, #4a1f0c)' : 'white',
        color: active ? 'white' : '#475569',
        whiteSpace: 'nowrap'
    });
    const tabs = [
        { id: 'todas', label: 'Todas', badgeBg: '#1f2937', badgeColor: 'white' },
        { id: 'pagadas', label: 'Pagadas', badgeBg: '#dcfce7', badgeColor: '#15803d' },
        { id: 'por_cobrar', label: 'Por cobrar', badgeBg: '#fef3c7', badgeColor: '#b45309' },
        { id: 'anuladas', label: 'Anuladas', badgeBg: '#fee2e2', badgeColor: '#dc2626' }
    ];
    const lines = [
        { id: 'todas', label: 'Todas las líneas' },
        { id: 'cafeteria', label: 'Punto de Venta' },
        { id: 'lonchera', label: 'Loncheras' },
        { id: 'cafe_grano', label: 'Café en Grano' }
    ];
    const datePresets = [
        { id: 'hoy', label: 'Hoy' },
        { id: 'ayer', label: 'Ayer' },
        { id: '7d', label: 'Últimos 7 días' },
        { id: 'mes', label: 'Este mes' },
        { id: 'todas', label: 'Todas' },
        { id: 'rango', label: 'Rango' }
    ];

    return (
        <div style={{ minHeight: '100vh', background: '#f8fafc', padding: '1rem' }}>
            <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                        <div style={{ ...panelStyle, width: '52px', height: '52px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', color: 'var(--primary-color, #4a1f0c)' }}>
                            <i className="bi bi-receipt-cutoff"></i>
                        </div>
                        <div>
                            <h1 style={{ fontSize: '26px', fontWeight: '800', color: '#1f2937', margin: 0 }}>Gestión de Órdenes</h1>
                            <p style={{ color: '#6b7280', margin: '2px 0 0', fontSize: '13px' }}>
                                Punto de Venta, Loncheras y Café en Grano · Pagadas, por cobrar (fiado) y anuladas
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={handleOpenSequenceModal}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 18px',
                            backgroundColor: 'var(--primary-color, #4a1f0c)', color: '#ffffff', border: 'none',
                            borderRadius: '10px', fontWeight: '600', fontSize: '14px', cursor: 'pointer',
                            boxShadow: '0 2px 6px rgba(74, 31, 12, 0.2)'
                        }}
                    >
                        <i className="bi bi-123"></i> Configurar Turno / N° Orden
                    </button>
                </div>

                {/* Pestañas por estado */}
                <div style={{ ...panelStyle, padding: '10px', marginBottom: '14px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {tabs.map(tab => {
                        const active = statusTab === tab.id;
                        return (
                            <button
                                key={tab.id}
                                type="button"
                                onClick={() => setStatusTab(tab.id)}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 16px', borderRadius: '10px',
                                    border: 'none', cursor: 'pointer', fontWeight: '700', fontSize: '14px',
                                    background: active ? '#1f2937' : 'transparent', color: active ? 'white' : '#475569'
                                }}
                            >
                                {tab.label}
                                <span style={{
                                    minWidth: '22px', padding: '1px 7px', borderRadius: '999px', fontSize: '12px', fontWeight: '800',
                                    background: active ? 'rgba(255,255,255,0.2)' : tab.badgeBg, color: active ? 'white' : tab.badgeColor
                                }}>
                                    {tabCounts[tab.id]}
                                </span>
                            </button>
                        );
                    })}
                </div>

                {/* Fechas, línea de negocio y búsqueda */}
                <div style={{ ...panelStyle, padding: '14px', marginBottom: '14px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <i className="bi bi-calendar3" style={{ color: '#64748b' }}></i>
                        {datePresets.map(p => (
                            <button key={p.id} type="button" onClick={() => setDatePreset(p.id)} style={chipStyle(datePreset === p.id)}>
                                {p.label}
                            </button>
                        ))}
                        {datePreset === 'rango' && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                                    style={{ padding: '6px 10px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '13px' }} />
                                <span style={{ color: '#94a3b8' }}>a</span>
                                <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                                    style={{ padding: '6px 10px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '13px' }} />
                            </div>
                        )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <i className="bi bi-funnel" style={{ color: '#64748b' }}></i>
                        {lines.map(l => (
                            <button key={l.id} type="button" onClick={() => setLineFilter(l.id)} style={chipStyle(lineFilter === l.id)}>
                                {l.label}
                            </button>
                        ))}
                    </div>
                    <div style={{ position: 'relative' }}>
                        <i className="bi bi-search" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}></i>
                        <input
                            type="text"
                            placeholder="Buscar por turno, número de orden, cliente, mesa, producto, escuela o vendedor..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                width: '100%', padding: '11px 16px 11px 40px', border: '1px solid #e5e7eb', borderRadius: '10px',
                                outline: 'none', fontSize: '14px', background: '#f8fafc'
                            }}
                        />
                    </div>
                </div>

                {/* Resumen del filtro actual */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '12px', marginBottom: '14px' }}>
                    {[
                        { label: 'Órdenes', value: summary.count, color: '#1f2937', icon: 'bi-receipt' },
                        { label: 'Total vendido', value: `$${summary.total.toFixed(2)}`, color: 'var(--primary-color, #4a1f0c)', icon: 'bi-cash-stack' },
                        { label: 'Cobrado', value: `$${summary.collected.toFixed(2)}`, color: '#15803d', icon: 'bi-check2-circle' },
                        { label: 'Por cobrar (fiado)', value: `$${summary.pending.toFixed(2)}`, color: '#b45309', icon: 'bi-hourglass-split' }
                    ].map(k => (
                        <div key={k.label} style={{ ...panelStyle, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <i className={`bi ${k.icon}`} style={{ fontSize: '20px', color: k.color }}></i>
                            <div>
                                <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{k.label}</div>
                                <div style={{ fontSize: '20px', fontWeight: '800', color: k.color }}>{k.value}</div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Calculadora de Órdenes Seleccionadas */}
                {selectedCount > 0 && (
                    <div style={{
                        backgroundColor: '#f0fdf4', border: '2px solid #2e7d32', borderRadius: '12px', padding: '16px 24px',
                        marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        flexWrap: 'wrap', gap: '16px', boxShadow: '0 4px 6px -1px rgba(46, 125, 50, 0.15)'
                    }}>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                <i className="bi bi-calculator-fill" style={{ fontSize: '18px' }}></i>
                                <span style={{ fontWeight: '700', fontSize: '16px', color: '#166534' }}>
                                    Sumador de Órdenes Seleccionadas ({selectedCount} órdenes)
                                </span>
                            </div>
                            <div style={{ display: 'flex', gap: '20px', fontSize: '14px', flexWrap: 'wrap', marginTop: '6px' }}>
                                <span style={{ color: '#15803d', fontWeight: '500' }}>Efectivo: <strong>${selectedCashUSD.toFixed(2)}</strong></span>
                                <span style={{ color: '#1e40af', fontWeight: '500' }}>Transferencia: <strong>${selectedTransferUSD.toFixed(2)}</strong></span>
                                <span style={{ color: '#6b21a8', fontWeight: '500' }}>Tarjeta: <strong>${selectedCardUSD.toFixed(2)}</strong></span>
                            </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                            <div style={{ textAlign: 'right' }}>
                                <div style={{ fontSize: '11px', color: '#166534', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Sumado</div>
                                <div style={{ fontSize: '28px', fontWeight: '800', color: '#2e7d32' }}>${selectedTotalUSD.toFixed(2)}</div>
                            </div>
                            <button
                                onClick={clearSelection}
                                style={{ padding: '8px 16px', borderRadius: '8px', backgroundColor: '#ffffff', border: '1.5px solid #cbd5e1', color: '#475569', fontWeight: '600', cursor: 'pointer', fontSize: '13px' }}
                            >
                                Limpiar Selección
                            </button>
                        </div>
                    </div>
                )}

                {/* Seleccionar todas las visibles */}
                {visibleOrders.length > 0 && (
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#475569', marginBottom: '10px', cursor: 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={isAllSelected}
                            onChange={() => toggleSelectAll(visibleOrders)}
                            style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#2e7d32' }}
                        />
                        Seleccionar todas las visibles para sumarlas
                    </label>
                )}

                {/* Tarjetas de órdenes agrupadas por día */}
                {visibleOrders.length === 0 ? (
                    <div style={{ ...panelStyle, padding: '48px 24px', textAlign: 'center' }}>
                        <i className="bi bi-inbox" style={{ fontSize: '36px', color: '#cbd5e1' }}></i>
                        <p style={{ color: '#6b7280', fontWeight: '500', fontSize: '15px', marginTop: '8px' }}>
                            {orders.length === 0 ? 'No hay órdenes registradas' : 'No hay órdenes con estos filtros'}
                        </p>
                        {orders.length > 0 && datePreset !== 'todas' && (
                            <button type="button" onClick={() => setDatePreset('todas')} style={{ ...chipStyle(false), marginTop: '6px' }}>
                                Ver todas las fechas
                            </button>
                        )}
                    </div>
                ) : (
                    ordersByDay.map(group => (
                        <div key={group.key} style={{ marginBottom: '22px' }}>
                            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '12px', margin: '6px 2px 12px', flexWrap: 'wrap' }}>
                                <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '800', color: '#1f2937' }}>
                                    {dayLabel(group)}
                                </h3>
                                <span style={{ fontSize: '13px', color: '#64748b' }}>
                                    {dayTotals[group.key]?.count} órdenes · <strong style={{ color: '#1f2937' }}>${(dayTotals[group.key]?.total || 0).toFixed(2)}</strong>
                                </span>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))', gap: '16px' }}>
                                {group.orders.map(order => {
                                    const state = getCardState(order);
                                    const line = getLineInfo(order);
                                    const sri = getSriInfo(order);
                                    const isSelected = selectedOrderIds.includes(order.id);
                                    const isCancelled = isCancelledOrder(order);
                                    const balance = pendingBalance(order);
                                    const infoRow = { display: 'flex', alignItems: 'center', gap: '7px', padding: '6px 10px', background: '#f8fafc', borderRadius: '8px', fontSize: '12px', color: '#64748b', minWidth: 0 };
                                    return (
                                        <div
                                            key={order.id}
                                            onClick={() => handleRowClick(order)}
                                            style={{
                                                position: 'relative', background: 'white', borderRadius: '14px', cursor: 'pointer',
                                                border: `1px solid ${isSelected ? '#2e7d32' : '#e5e7eb'}`,
                                                borderLeft: `5px solid ${state.color}`,
                                                boxShadow: isSelected ? '0 0 0 3px rgba(46,125,50,0.18)' : '0 1px 3px rgba(15,23,42,0.06)',
                                                padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px',
                                                opacity: isCancelled ? 0.75 : 1, transition: 'transform 0.15s, box-shadow 0.15s'
                                            }}
                                            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; }}
                                        >
                                            {/* Encabezado: número de orden + cliente */}
                                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                                                <div style={{
                                                    background: '#111827', color: 'white', borderRadius: '8px', padding: '6px 10px',
                                                    fontWeight: '800', fontSize: order.order_sequence ? '18px' : '12px', lineHeight: 1.2,
                                                    maxWidth: '55%', wordBreak: 'break-all', flexShrink: 0
                                                }} title={order.order_number}>
                                                    {order.order_sequence ? `#${order.order_sequence}` : order.order_number}
                                                </div>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    {/* Cliente (ventas fiadas / con cliente) o, si no hay, el trabajador que vendió */}
                                                    <div
                                                        style={{ fontWeight: '700', color: '#1f2937', fontSize: '14px', lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                                        title={order.customer_name ? 'Cliente' : order.seller_name ? 'Vendido por' : ''}
                                                    >
                                                        {order.customer_name ? (
                                                            order.customer_name
                                                        ) : order.seller_name ? (
                                                            <><i className="bi bi-person-badge" style={{ color: '#64748b', marginRight: '4px' }}></i>{order.seller_name}</>
                                                        ) : 'Consumidor Final'}
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '3px', fontSize: '12px', color: '#64748b', flexWrap: 'wrap' }}>
                                                        <span>{formatTime(order.created_at)}</span>
                                                        <span style={{ padding: '1px 7px', borderRadius: '6px', fontSize: '11px', fontWeight: '700', background: line.bg, color: line.color }}>
                                                            {line.label}
                                                        </span>
                                                    </div>
                                                </div>
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onChange={() => toggleSelectOrder(order.id)}
                                                    title="Seleccionar para sumar"
                                                    style={{ width: '17px', height: '17px', cursor: 'pointer', accentColor: '#2e7d32', flexShrink: 0 }}
                                                />
                                            </div>

                                            {/* Productos */}
                                            <div style={{
                                                padding: '9px 11px', background: '#f8fafc', border: '1px solid #f1f5f9', borderRadius: '9px',
                                                fontSize: '13px', fontWeight: '600', color: '#334155', lineHeight: 1.35,
                                                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'
                                            }} title={order.items_summary}>
                                                {order.items_summary || `${order.items_count || 0} producto(s)`}
                                            </div>

                                            {/* Detalles */}
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                                <div style={infoRow}>
                                                    <i className={`bi ${line.icon}`} style={{ color: line.color }}></i>
                                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#334155', fontWeight: '600' }} title={line.detail}>{line.detail}</span>
                                                </div>
                                                <div style={infoRow}>
                                                    <i className="bi bi-clock"></i>
                                                    <span>Registro: {formatShortDateTime(order.created_at)}</span>
                                                </div>
                                                {order.seller_name && order.customer_name && (
                                                    <div style={infoRow}>
                                                        <i className="bi bi-person-badge"></i>
                                                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Vendió: {order.seller_name}</span>
                                                    </div>
                                                )}
                                                <div style={{ ...infoRow, color: sri.color }} title={sri.title || ''}>
                                                    <i className={`bi ${sri.icon}`}></i>
                                                    {sri.href ? (
                                                        <a href={sri.href} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
                                                            style={{ color: sri.color, fontWeight: '600', textDecoration: 'none' }}>
                                                            {sri.text} (PDF)
                                                        </a>
                                                    ) : <span>{sri.text}</span>}
                                                </div>
                                                {balance > 0 && (
                                                    <div style={{ ...infoRow, background: '#fffbeb', color: '#b45309', fontWeight: '700' }}>
                                                        <i className="bi bi-hourglass-split"></i>
                                                        <span>Saldo por cobrar: ${balance.toFixed(2)}</span>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Total, forma de pago y estado */}
                                            <div style={{ marginTop: 'auto', paddingTop: '10px', borderTop: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
                                                <span style={{ fontSize: '22px', fontWeight: '800', color: '#111827', textDecoration: isCancelled ? 'line-through' : 'none' }}>
                                                    {formatOrderValue(order.total, order.payment_method)}
                                                </span>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                                    {getPaymentMethodBadge(order.payment_method)}
                                                    <span style={{ padding: '4px 9px', borderRadius: '6px', fontSize: '11px', fontWeight: '800', background: state.bg, color: state.color, letterSpacing: '0.03em' }}>
                                                        {state.label}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Estado de preparación (no aplica a órdenes anuladas) */}
                                            {!isCancelled && (
                                                <select
                                                    value={order.status === 'pending' ? 'pending' : 'completed'}
                                                    onChange={(e) => handleStatusChange(order.order_number, e.target.value, e)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    disabled={updatingStatus[order.order_number]}
                                                    title="Estado de la orden"
                                                    style={{
                                                        padding: '6px 10px', borderRadius: '8px', fontSize: '12px', fontWeight: '600',
                                                        border: '1px solid #e5e7eb', background: 'white', color: '#475569',
                                                        cursor: updatingStatus[order.order_number] ? 'wait' : 'pointer'
                                                    }}
                                                >
                                                    <option value="pending">Pendiente de entrega</option>
                                                    <option value="completed">Entregada / Completada</option>
                                                </select>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))
                )}

                {/* Cargar más */}
                {sortedAndFilteredOrders.length > visibleOrders.length && (
                    <div style={{ textAlign: 'center', margin: '8px 0 24px' }}>
                        <p style={{ color: '#64748b', fontSize: '13px' }}>
                            Mostrando {visibleOrders.length} de {sortedAndFilteredOrders.length} órdenes
                        </p>
                        <button
                            type="button"
                            onClick={() => setVisibleCount(c => c + PAGE_SIZE)}
                            style={{ ...chipStyle(true), padding: '10px 22px', fontSize: '14px' }}
                        >
                            Cargar más órdenes
                        </button>
                    </div>
                )}
            </div>

            {/* Order Details Modal */}
            {showModal && selectedOrder && (
                <div
                    onClick={closeModal}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000,
                        padding: '20px'
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            backgroundColor: 'white',
                            borderRadius: '16px',
                            maxWidth: '600px',
                            width: '100%',
                            maxHeight: '90vh',
                            overflow: 'auto',
                            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                        }}
                    >
                        {/* Modal Header */}
                        <div style={{ padding: '24px', borderBottom: '1px solid #e5e7eb' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                                <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        {selectedOrder.order_sequence && (
                                            <span style={{
                                                padding: '4px 10px',
                                                borderRadius: '8px',
                                                fontSize: '16px',
                                                fontWeight: '800',
                                                backgroundColor: '#166534',
                                                color: '#ffffff'
                                            }}>
                                                ORDEN #{selectedOrder.order_sequence}
                                            </span>
                                        )}
                                        <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>
                                            {selectedOrder.order_number}
                                        </h2>
                                    </div>
                                    <p style={{ color: '#6b7280', fontSize: '14px', marginTop: '4px', marginBottom: 0 }}>
                                        {new Date(selectedOrder.created_at).toLocaleString('es-ES', {
                                            day: '2-digit',
                                            month: '2-digit',
                                            year: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                        })}
                                    </p>
                                </div>
                                <button
                                    onClick={closeModal}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        fontSize: '24px',
                                        cursor: 'pointer',
                                        color: '#6b7280',
                                        padding: '4px 8px',
                                        lineHeight: 1
                                    }}
                                    aria-label="Cerrar modal"
                                >
                                    ×
                                </button>
                                
                                <button
                                    onClick={handlePrintTicket}
                                    style={{
                                        background: 'var(--primary-color)',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '4px',
                                        padding: '8px 16px',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        marginLeft: '8px'
                                    }}
                                    aria-label="Imprimir orden"
                                >
                                    Imprimir
                                </button>
                            </div>

                            {/* Cliente destacado en el header */}
                            <div style={{
                                backgroundColor: '#eff6ff',
                                padding: '12px 16px',
                                borderRadius: '8px',
                                border: '1px solid #bfdbfe',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}>
                                <svg
                                    style={{ width: '20px', height: '20px', color: 'var(--primary-color, #4a1f0c)' }}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                                <div>
                                    <p style={{ margin: 0, fontSize: '12px', color: '#1e40af', fontWeight: '500' }}>Cliente</p>
                                    <p style={{ margin: 0, fontSize: '16px', color: '#1e3a8a', fontWeight: '600' }}>
                                        {selectedOrder.customer_name || 'Cliente Casual'}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Modal Body */}
                        <div style={{ padding: '24px' }}>
                            {/* Order Info */}
                            <div style={{ marginBottom: '24px' }}>
                                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                                    Información de la Orden
                                </h3>
                                <div style={{ backgroundColor: 'var(--sidebar-bg)', padding: '16px', borderRadius: '8px' }}>
                                    <p style={{ margin: '0 0 8px 0', color: '#1f2937' }}>
                                        <strong>Tipo de Orden:</strong> {selectedOrder.order_type_display}
                                    </p>
                                    <p style={{ margin: '0 0 8px 0', color: '#1f2937' }}>
                                        <strong>Estado:</strong>{' '}
                                        <span style={{
                                            padding: '4px 10px',
                                            borderRadius: '9999px',
                                            fontSize: '12px',
                                            fontWeight: '500',
                                            backgroundColor: (selectedOrder.status_display?.toLowerCase() === 'anulada' || selectedOrder.status?.toLowerCase() === 'cancelled') ? '#fee2e2' :
                                                             (selectedOrder.status_display?.toLowerCase() === 'completado' || selectedOrder.status?.toLowerCase() === 'completed') ? '#dcfce7' : '#fef3c7',
                                            color: (selectedOrder.status_display?.toLowerCase() === 'anulada' || selectedOrder.status?.toLowerCase() === 'cancelled') ? '#991b1b' :
                                                   (selectedOrder.status_display?.toLowerCase() === 'completado' || selectedOrder.status?.toLowerCase() === 'completed') ? '#166534' : '#92400e'
                                        }}>
                                            {selectedOrder.status_display || getStatusDisplay(selectedOrder.status)}
                                        </span>
                                    </p>
                                    {selectedOrder.table_number && (
                                        <p style={{ margin: '0', color: '#1f2937' }}>
                                            <strong>Mesa:</strong> {selectedOrder.table_number}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Order Items */}
                            <div style={{ marginBottom: '24px' }}>
                                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                                    Productos
                                </h3>
                                <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                                    {selectedOrder.loading ? (
                                        <div style={{ padding: '32px', textAlign: 'center' }}>
                                            <div style={{
                                                display: 'inline-block',
                                                width: '32px',
                                                height: '32px',
                                                border: '3px solid #e5e7eb',
                                                borderTopColor: 'var(--primary-color, #4a1f0c)',
                                                borderRadius: '50%',
                                                animation: 'spin 1s linear infinite'
                                            }}></div>
                                            <p style={{ marginTop: '12px', color: '#6b7280' }}>Cargando productos...</p>
                                        </div>
                                    ) : (
                                        // MODO VISUALIZACIÓN: LISTA NORMAL (Código Original)
                                        selectedOrder.items && selectedOrder.items.length > 0 ? (
                                            selectedOrder.items.map((item, index) => {
                                                const hasDiscount = parseFloat(item.discount_percentage) > 0;
                                                const originalTotal = parseFloat(item.unit_price) * item.quantity;
                                                return (
                                                    <div
                                                        key={index}
                                                        style={{
                                                            padding: '12px 16px',
                                                            borderBottom: index < selectedOrder.items.length - 1 ? '1px solid #e5e7eb' : 'none',
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center'
                                                        }}
                                                    >
                                                        <div>
                                                            <p style={{ margin: 0, fontWeight: '500', color: '#1f2937' }}>
                                                                {item.product_details?.name || item.product_name || 'Producto'}
                                                            </p>
                                                            <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6b7280' }}>
                                                                Cantidad: {item.quantity} × {formatOrderValue(item.unit_price, selectedOrder.payment_method)}
                                                                {hasDiscount && (
                                                                    <span style={{ marginLeft: '8px', color: '#10b981', fontWeight: '500' }}>
                                                                        (Desc: {parseFloat(item.discount_percentage)}%)
                                                                    </span>
                                                                )}
                                                            </p>
                                                            {item.notes && (
                                                                <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#d97706', fontStyle: 'italic', backgroundColor: '#fffbeb', padding: '2px 6px', borderRadius: '4px', display: 'inline-block' }}>
                                                                    Nota: {item.notes}
                                                                </p>
                                                            )}
                                                        </div>
                                                        <div style={{ textAlign: 'right' }}>
                                                            {hasDiscount && (
                                                                <p style={{ margin: '0 0 2px 0', fontSize: '12px', textDecoration: 'line-through', color: '#9ca3af' }}>
                                                                    {formatOrderValue(originalTotal, selectedOrder.payment_method)}
                                                                </p>
                                                            )}
                                                            <p style={{ margin: 0, fontWeight: '600', color: hasDiscount ? '#10b981' : 'var(--primary-color)' }}>
                                                                {formatOrderValue(item.line_total || item.subtotal, selectedOrder.payment_method)}
                                                            </p>
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <p style={{ padding: '16px', margin: 0, color: '#6b7280', textAlign: 'center' }}>
                                                No hay productos en esta orden
                                            </p>
                                        )
                                    )}
                                </div>
                            </div>

                            {/* Order Summary */}
                            <div style={{ backgroundColor: 'var(--sidebar-bg)', padding: '16px', borderRadius: '8px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                    <span style={{ color: '#6b7280' }}>Subtotal:</span>
                                    <span style={{ fontWeight: '500', color: '#1f2937' }}>{formatOrderValue(selectedOrder.subtotal, selectedOrder.payment_method)}</span>
                                </div>
                                {selectedOrder.tax_amount > 0 && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                        <span style={{ color: '#6b7280' }}>Impuestos:</span>
                                        <span style={{ fontWeight: '500', color: '#1f2937' }}>{formatOrderValue(selectedOrder.tax_amount, selectedOrder.payment_method)}</span>
                                    </div>
                                )}
                                {selectedOrder.discount_amount > 0 && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                        <span style={{ color: '#6b7280' }}>Descuento:</span>
                                        <span style={{ fontWeight: '500', color: '#ef4444' }}>-{formatOrderValue(selectedOrder.discount_amount, selectedOrder.payment_method)}</span>
                                    </div>
                                )}
                                <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '8px', marginTop: '8px', display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>Total:</span>
                                    <span style={{ fontSize: '18px', fontWeight: '700', color: 'var(--primary-color)' }}>{formatOrderValue(selectedOrder.total, selectedOrder.payment_method)}</span>
                                </div>
                            </div>

                            {/* Información de Pago */}
                            <div style={{ marginTop: '24px' }}>
                                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#374151', marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    Información de Pago
                                    {!isEditingPayment && (
                                        <button 
                                            onClick={() => {
                                                setNewPaymentMethod(selectedOrder.payment_method || 'efectivo');
                                                setIsEditingPayment(true);
                                            }} 
                                            style={{ background: 'none', border: 'none', color: 'var(--primary-color)', cursor: 'pointer', fontSize: '14px' }}
                                        >
                                            <i className="fas fa-edit"></i> Editar
                                        </button>
                                    )}
                                </h3>
                                <div style={{ backgroundColor: '#f3f4f6', padding: '16px', borderRadius: '8px' }}>
                                    {isEditingPayment ? (
                                        <div>
                                            <select 
                                                value={newPaymentMethod} 
                                                onChange={(e) => setNewPaymentMethod(e.target.value)}
                                                style={{ width: '100%', padding: '8px', marginBottom: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
                                            >
                                                <option value="efectivo">Efectivo</option>
                                                <option value="transferencia">Transferencia</option>
                                                <option value="dividido">Mixto</option>
                                                <option value="tarjeta">Tarjeta</option>
                                            </select>
                                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                                <button onClick={() => setIsEditingPayment(false)} style={{ padding: '6px 12px', borderRadius: '4px', border: '1px solid #d1d5db', background: 'white', cursor: 'pointer' }}>Cancelar</button>
                                                <button onClick={handleChangePaymentMethod} style={{ padding: '6px 12px', borderRadius: '4px', border: 'none', background: 'var(--primary-color)', color: 'white', cursor: 'pointer' }}>Guardar</button>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <p style={{ margin: '0 0 8px 0', color: '#1f2937' }}>
                                                <strong>Método de Pago:</strong> <span style={{ textTransform: 'capitalize' }}>{selectedOrder.payment_method || 'Efectivo'}</span>
                                            </p>
                                            {selectedOrder.payment_method === 'transferencia' && selectedOrder.payment_reference && (
                                                <p style={{ margin: '0', color: '#1f2937' }}>
                                                    <strong>Referencia:</strong> {selectedOrder.payment_reference}
                                                </p>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Notes */}


                            {/* SECCIÓN FACTURACIÓN ELECTRÓNICA SRI (KoaDoc) */}
                            <div style={{ marginTop: '24px' }}>
                                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#374151', marginBottom: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <span><i className="bi bi-file-earmark-check" style={{ color: 'var(--primary-color)' }}></i> Facturación Electrónica SRI</span>
                                    {selectedOrder.sri_status === 'AUTHORIZED' ? (
                                        <span style={{ fontSize: '12px', color: '#15803d', backgroundColor: '#dcfce7', padding: '3px 8px', borderRadius: '10px' }}>
                                            Autorizado SRI
                                        </span>
                                    ) : selectedOrder.sri_status === 'QUEUED' ? (
                                        <span style={{ fontSize: '12px', color: '#0369a1', backgroundColor: '#e0f2fe', padding: '3px 8px', borderRadius: '10px' }}>
                                            En Cola SRI
                                        </span>
                                    ) : (
                                        <span style={{ fontSize: '12px', color: '#6b7280', backgroundColor: '#f3f4f6', padding: '3px 8px', borderRadius: '10px' }}>
                                            {selectedOrder.sri_status || 'No Emitida'}
                                        </span>
                                    )}
                                </h3>
                                <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', padding: '16px', borderRadius: '8px' }}>
                                    {selectedOrder.sri_document_number && (
                                        <p style={{ margin: '0 0 6px 0', fontSize: '14px', color: '#1f2937' }}>
                                            <strong>N° Comprobante:</strong> {selectedOrder.sri_document_number}
                                        </p>
                                    )}
                                    {selectedOrder.sri_access_key && (
                                        <p style={{ margin: '0 0 6px 0', fontSize: '12px', color: '#475569', wordBreak: 'break-all' }}>
                                            <strong>Clave de Acceso:</strong> <code>{selectedOrder.sri_access_key}</code>
                                        </p>
                                    )}
                                    {selectedOrder.sri_error_message && (
                                        <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: selectedOrder.sri_status === 'AUTHORIZED' ? '#15803d' : '#b91c1c' }}>
                                            <strong>Mensaje:</strong> {selectedOrder.sri_error_message}
                                        </p>
                                    )}

                                    <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
                                        {selectedOrder.sri_pdf_url && (
                                            <a
                                                href={selectedOrder.sri_pdf_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{
                                                    padding: '6px 12px',
                                                    borderRadius: '6px',
                                                    backgroundColor: '#dcfce7',
                                                    color: '#15803d',
                                                    border: '1px solid #86efac',
                                                    fontSize: '13px',
                                                    fontWeight: '600',
                                                    textDecoration: 'none',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '4px'
                                                }}
                                            >
                                                <i className="bi bi-file-earmark-pdf-fill"></i> Descargar RIDE (PDF)
                                            </a>
                                        )}
                                        {selectedOrder.sri_xml_url && (
                                            <a
                                                href={selectedOrder.sri_xml_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{
                                                    padding: '6px 12px',
                                                    borderRadius: '6px',
                                                    backgroundColor: '#f1f5f9',
                                                    color: '#475569',
                                                    border: '1px solid #cbd5e1',
                                                    fontSize: '13px',
                                                    fontWeight: '600',
                                                    textDecoration: 'none',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '4px'
                                                }}
                                            >
                                                <i className="bi bi-code-square"></i> Descargar XML
                                            </a>
                                        )}
                                        {selectedOrder.sri_id && (
                                            <button
                                                type="button"
                                                onClick={async () => {
                                                    try {
                                                        const res = await billingService.getOrderInvoiceStatus(selectedOrder.id);
                                                        if (res.success) {
                                                            alert(`Estado actual SRI: ${res.data?.status || 'Actualizado'}`);
                                                            fetchOrders();
                                                            closeModal();
                                                        } else {
                                                            alert(res.message || 'No se pudo actualizar el estado.');
                                                        }
                                                    } catch (err) {
                                                        alert('Error al consultar estado: ' + (err.response?.data?.message || err.message));
                                                    }
                                                }}
                                                style={{
                                                    padding: '6px 12px',
                                                    borderRadius: '6px',
                                                    backgroundColor: '#f1f5f9',
                                                    color: '#334155',
                                                    border: '1px solid #cbd5e1',
                                                    fontSize: '13px',
                                                    fontWeight: '600',
                                                    cursor: 'pointer',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '4px'
                                                }}
                                            >
                                                <i className="bi bi-arrow-clockwise"></i> Consultar Estado SRI
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            onClick={async () => {
                                                try {
                                                    const res = await billingService.emitOrderInvoice(selectedOrder.id);
                                                    alert(res.message || 'Factura emitida con éxito.');
                                                    fetchOrders();
                                                    closeModal();
                                                } catch (err) {
                                                    alert('Error al emitir factura: ' + (err.response?.data?.message || err.message));
                                                }
                                            }}
                                            style={{
                                                padding: '6px 12px',
                                                borderRadius: '6px',
                                                backgroundColor: 'var(--primary-color)',
                                                color: 'white',
                                                border: 'none',
                                                fontSize: '13px',
                                                fontWeight: '600',
                                                cursor: 'pointer',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '4px'
                                            }}
                                        >
                                            <i className="bi bi-send-fill"></i> {selectedOrder.sri_status === 'AUTHORIZED' ? 'Reenviar Factura' : 'Emitir al SRI'}
                                        </button>
                                    </div>
                                </div>
                            </div>


                            {/* SECCIÓN DE EDICIÓN DE NOTAS */}
                            {selectedOrder.notes && (
                                <div style={{ marginTop: '24px' }}>
                                    <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                                        Notas
                                    </h3>
                                    <p style={{ backgroundColor: '#fef3c7', padding: '12px', borderRadius: '8px', margin: 0, color: '#92400e' }}>
                                        {formatOrderNotes(selectedOrder.notes)}
                                    </p>
                                </div>
                            )}


                            {/* BOTONES DE ACCIÓN (FOOTER) */}
                            <div style={{ marginTop: '32px', borderTop: '1px solid #e5e7eb', paddingTop: '24px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                                {isAdmin && selectedOrder.status !== 'cancelled' && selectedOrder.status_display?.toLowerCase() !== 'anulada' && (
                                    <button
                                        onClick={handleCancelOrder}
                                        style={{
                                            padding: '10px 20px',
                                            backgroundColor: '#fee2e2',
                                            color: '#b91c1c',
                                            border: '1px solid #fca5a5',
                                            borderRadius: '8px',
                                            fontWeight: '600',
                                            cursor: 'pointer',
                                            marginRight: 'auto'
                                        }}
                                    >
                                        Anular Orden
                                    </button>
                                )}
                                <button
                                    onClick={closeModal}
                                    style={{
                                        padding: '10px 20px',
                                        backgroundColor: 'var(--sidebar-bg)',
                                        color: '#374151',
                                        border: 'none',
                                        borderRadius: '8px',
                                        fontWeight: '600',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Cerrar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal para configurar número de orden secuencial y reinicio diario */}
            <ConfiguracionSecuenciaModal
                isOpen={showSequenceModal}
                onClose={() => setShowSequenceModal(false)}
            />


            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

export default Ordenes;